// Webhooks index (task [3.6], ws-gp3r9) — a paginated table of endpoint configs
// (Endpoint / Status / Created) built on the shared Table atoms at delivery-log's
// density. Row-click drills into `/webhooks/:id`, handing the clicked config
// across the navigation in `location.state` (the detail page's stable-lookup
// seam). Creation is out of scope: the "+ Add webhook" button is a disabled
// placeholder.
//
// The config pool is tiny (3 rows today) so there is no search/filter machinery
// — just the table plus a "Load more" control when the infinite query reports a
// further page. CREATED uses the same coarse relative formatter the detail page
// carries (no shared util exists).
import { AlertCircle, ChevronRight, Inbox, Plus, Webhook } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useWebhookConfigs } from '@/api/webhooks/hooks';
import type { WebhookConfig } from '@/api/webhooks/mock';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';

const relativeFmt = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

/** Coarse "N units ago" from an ISO timestamp — display only. */
function formatRelative(iso: string): string {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '—';
	const deltaSec = Math.round((then - Date.now()) / 1000);
	const abs = Math.abs(deltaSec);
	const steps: [Intl.RelativeTimeFormatUnit, number][] = [
		['year', 60 * 60 * 24 * 365],
		['month', 60 * 60 * 24 * 30],
		['day', 60 * 60 * 24],
		['hour', 60 * 60],
		['minute', 60],
	];
	for (const [unit, secs] of steps) {
		if (abs >= secs) return relativeFmt.format(Math.round(deltaSec / secs), unit);
	}
	return relativeFmt.format(deltaSec, 'second');
}

function AddWebhookButton() {
	return (
		<button
			type="button"
			disabled
			aria-disabled="true"
			title="Creating webhooks is not available yet"
			data-slot="button"
			className="inline-flex items-center gap-1.5 border border-border bg-secondary px-3 py-1.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-40"
		>
			<Plus className="size-4" aria-hidden="true" />
			Add webhook
		</button>
	);
}

export default function WebhooksPage() {
	const navigate = useNavigate();
	const {
		data,
		isLoading,
		isError,
		refetch,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useWebhookConfigs();

	const configs: WebhookConfig[] =
		data?.pages.flatMap((p) => p.configs) ?? [];

	function onRowClick(config: WebhookConfig) {
		void navigate(`/webhooks/${config.id}`, {
			viewTransition: true,
			state: { config },
		});
	}

	return (
		<PageContainer>
			<div className="flex items-start justify-between gap-4">
				<PageHeader
					title="Webhooks"
					description="Endpoints receiving your email event notifications."
				/>
				<AddWebhookButton />
			</div>

			{isLoading ? (
				<WebhooksSkeleton />
			) : isError ? (
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<AlertCircle className="size-10 text-destructive" aria-hidden="true" />
					<p className="text-sm text-muted-foreground">
						Couldn’t load webhook endpoints.
					</p>
					<button
						type="button"
						onClick={() => void refetch()}
						data-slot="button"
						className="border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
					>
						Try again
					</button>
				</div>
			) : configs.length === 0 ? (
				<div className="flex flex-col items-center gap-3 py-16 text-center">
					<Inbox className="size-10 text-muted-foreground" aria-hidden="true" />
					<div>
						<h3 className="font-semibold text-foreground">No webhooks yet</h3>
						<p className="text-sm text-muted-foreground">
							Endpoints you create will appear here.
						</p>
					</div>
				</div>
			) : (
				<>
					<div className="overflow-x-auto rounded-lg border border-border">
						<Table className="text-left">
							<TableHeader className="bg-muted/40">
								<TableRow className="text-xs uppercase tracking-wide text-muted-foreground hover:bg-transparent">
									<TableHead className="px-4 py-1.5">Endpoint</TableHead>
									<TableHead className="px-4 py-1.5">Status</TableHead>
									<TableHead className="px-4 py-1.5">Created</TableHead>
									<TableHead className="w-10 px-4 py-1.5">
										<span className="sr-only">Open</span>
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{configs.map((config) => (
									<TableRow
										key={config.id}
										onClick={() => onRowClick(config)}
										className="group cursor-pointer hover:bg-accent/40"
									>
										<TableCell className="px-4 py-1.5">
											<div className="flex items-center gap-2">
												<Webhook
													className="size-4 shrink-0 text-muted-foreground"
													aria-hidden="true"
												/>
												<span className="truncate font-mono text-xs text-foreground">
													{config.url}
												</span>
											</div>
										</TableCell>
										<TableCell className="px-4 py-1.5">
											<Badge
												status={
													config.status === 'enabled' ? 'success' : 'neutral'
												}
											>
												{config.status === 'enabled' ? 'Enabled' : 'Disabled'}
											</Badge>
										</TableCell>
										<TableCell
											className="whitespace-nowrap px-4 py-1.5 text-muted-foreground"
											suppressHydrationWarning
										>
											{formatRelative(config.createdAt)}
										</TableCell>
										<TableCell className="px-4 py-1.5 text-right">
											<ChevronRight
												className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
												aria-hidden="true"
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					{hasNextPage ? (
						<div className="flex justify-center">
							<button
								type="button"
								data-slot="button"
								onClick={() => void fetchNextPage()}
								disabled={isFetchingNextPage}
								className="border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
							>
								{isFetchingNextPage ? 'Loading…' : 'Load more'}
							</button>
						</div>
					) : null}
				</>
			)}
		</PageContainer>
	);
}

function WebhooksSkeleton() {
	return (
		<div className="space-y-3">
			{Array.from({ length: 3 }).map((_, i) => (
				<div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
			))}
		</div>
	);
}
